/**
 * Unified Date/Time Utilities
 * Consolidates all date/time formatting functions used throughout the application
 */

// Re-export from date-fns for consistency
export { format, parseISO, isValid } from 'date-fns'

/**
 * Format date as MM/DD/YYYY
 * Handles timezone issues by treating dates as local
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    // Handle YYYY-MM-DD format directly to avoid timezone issues
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      // Convert to MM/DD/YYYY format directly
      return `${month}/${day}/${year}`;
    }
    
    // For other date formats, use the Date object but ensure it's treated as local
    const date = new Date(dateString + 'T00:00:00'); // Add time to prevent timezone conversion
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Format time with 12h/24h option
 * Main time formatting function used throughout the app
 */
export const formatTime = (time: Date | string, format: '12h' | '24h' = '12h'): string => {
  if (!time) return '';
  
  try {
    let timeString: string;
    
    if (time instanceof Date) {
      timeString = time.toTimeString().slice(0, 5); // Get HH:MM format
    } else {
      timeString = time;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (format === '12h') {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } else {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  } catch (error) {
    console.error('Error formatting time:', error);
    return typeof time === 'string' ? time : '';
  }
};

/**
 * Format time display - alias for formatTime for backward compatibility
 * @deprecated Use formatTime instead
 */
export const formatTimeDisplay = (timeString: string, timeFormat: '12h' | '24h' = '12h'): string => {
  return formatTime(timeString, timeFormat);
};

/**
 * Format duration in minutes to human-readable format
 * Examples: 90 -> "1h 30m", 45 -> "45m", 120 -> "2h"
 */
export const formatDuration = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '0m';
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
};

/**
 * Calculate practice duration in minutes
 */
export const calculatePracticeDuration = (startTime: string, endTime: string): number => {
  if (!startTime || !endTime) return 0;
  
  try {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;

    // Handle cases where practice ends the next day
    const duration = endTotalMinutes >= startTotalMinutes
      ? endTotalMinutes - startTotalMinutes
      : (24 * 60 - startTotalMinutes) + endTotalMinutes;

    return duration;
  } catch (error) {
    console.error('Error calculating practice duration:', error);
    return 0;
  }
};

/**
 * Format minutes since practice start into time format
 * Used for displaying drill start/end times relative to practice start
 */
export const formatMinutesSincePracticeStart = (
  minutes: number, 
  startTime: string,
  timeFormat: '12h' | '24h' = '12h'
): string => {
  if (minutes === null || minutes === undefined || !startTime) return '';
  
  try {
    const [hours, mins] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;

    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;

    return formatTime(`${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`, timeFormat);
  } catch (error) {
    console.error('Error formatting minutes since practice start:', error);
    return '';
  }
};

/**
 * Get current time in HH:MM format
 */
export const getCurrentTime = (): string => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
};

/**
 * Parse time string to minutes since midnight
 */
export const timeToMinutes = (timeString: string): number => {
  if (!timeString) return 0;
  
  try {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  } catch (error) {
    console.error('Error parsing time to minutes:', error);
    return 0;
  }
};

/**
 * Convert minutes since midnight to time string
 */
export const minutesToTime = (minutes: number): string => {
  if (minutes < 0) return '00:00';
  
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

/**
 * Check if a time string is valid (HH:MM format)
 */
export const isValidTime = (timeString: string): boolean => {
  if (!timeString) return false;
  
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(timeString);
};

/**
 * Format date for display in practice lists and forms
 */
export const formatDisplayDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting display date:', error);
    return dateString;
  }
};

/**
 * Get time format preference from localStorage
 */
export const getTimeFormatPreference = (): '12h' | '24h' => {
  return localStorage.getItem('timeFormat') === '24h' ? '24h' : '12h';
};